import React, { useContext, useState } from 'react';
import InputValidatorField from '../../../common/components/Inputs/InputValidatorField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import {IsValidEmail, IsValidPassword} from '../../../../utils/Validators';
import { ThemeContext } from 'styled-components';
import ApiClient from '../../../../services/ApiClient';
import NewUUID from '../../../../utils/randy/NewUUID';
import { parseClaimsFromHeader } from '../../../../utils/auth/ClaimsParser';
import InputEmail from "../../../common/components/Inputs/InputEmail";
import InputPassword from "../../../common/components/Inputs/InputPassword";
import WithActions from "../../../common/components/Wrappers/WithActions";
import ButtonPrimary from "../../../common/components/Buttons/ButtonPrimary";
import DisabledFormState from "../../../common/components/Wrappers/DisabledFormState";
import TurnstileWidget from "../../../common/components/Turnstile/TurnstileWidget";
import { resetTurnstile } from "../../../../services/turnstile";

const LoginForm = ({
    formData,
    onChangeAuthForm,
    handleLogin,
    onStepTransition,
    onLoginSuccess,
    onLoginError,
    isLoading,
    setIsLoading,
    csrfToken,
    disabled = false,
    isVisible = true,
}) => {
    const theme = useContext(ThemeContext);
    const [localError, setLocalError] = useState(null);

    // Safety check for formData
    if (!formData) {
        return <div>Loading...</div>;
    }

    // Don't render if not visible
    if (!isVisible) {
        return null;
    }

    if (disabled) {
        return <DisabledFormState message="Authenticated" tooltip="Added" />
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLoading) {
            return;
        }

        // Validate form and show errors if invalid
        if (!formData.isValid) {
            // Trigger form validation errors by setting local error
            if (!IsValidEmail(formData.email)) {
                setLocalError("Please enter a valid email address");
                return;
            }
            if (!IsValidPassword(formData.password)) {
                setLocalError("Please enter a valid password");
                return;
            }
            setLocalError("Please fill in all required fields");
            return;
        }

        // If this is being used in a forgot flow context (no csrfToken), don't attempt login
        if (!csrfToken) {
            return;
        }

        setIsLoading(true);
        setLocalError(null);

        try {
            await handleLogin(
                formData.email,
                formData.password,
                {
                    onMfaSetup: (mfaData) => {
                        onStepTransition('setupMFA', null, mfaData);
                        setIsLoading(false);
                    },
                    onMfaVerify: (mfaData) => {
                        onStepTransition('verifyMFA', null, mfaData);
                        setIsLoading(false);
                    },
                    onError: (error) => {
                        // Tokens are single use and this path leaves the user on the
                        // form; without a reset the retry would send an already-redeemed
                        // token and be refused as timeout-or-duplicate.
                        resetTurnstile();
                        if (error === "user is not confirmed") {
                            onStepTransition('verifyEmail');
                        } else {
                            onLoginError(error);
                        }
                        setIsLoading(false);
                    },
                    onSuccess: (claims) => {
                        onLoginSuccess({ ...claims, email: formData.email });
                        setIsLoading(false);
                    }
                }
            );
        } catch (err) {
            console.error('Login error:', err);
            resetTurnstile();
            onLoginError("Login failed. Please try again.");
            setIsLoading(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                minHeight: 0,
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                borderRadius: '8px',
                transition: 'all 0.3s ease'
            }}
        >
            <TurnstileWidget />

            {/* Form fields - Scrollable area */}
            <Box
                sx={{
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflowY: 'auto',
                    mb: 2
                }}
            >
                <WithActions
                    showSuccessButton={disabled}
                    isVisible={true}
                    successTooltip="verified"
                    inputType="text"
                    forceSpacing={true}
                    fullWidth
                >
                    <InputEmail
                        email={formData.email || ''}
                        onEmailChange={onChangeAuthForm}
                        showValidation={false}
                    />
                </WithActions>
                <WithActions
                    disabled={disabled || isLoading}
                    showSuccessButton={disabled}
                    isVisible={!disabled}
                    successTooltip="verified"
                    inputType="text"
                    forceSpacing={true}
                    fullWidth
                >
                    <InputPassword
                        handleChange={onChangeAuthForm}
                    requirementsText={[
                        "",
                        <div>
                            Password must include:
                            <br />* 12 - 36 Characters
                            <br />* Uppercase & Lowercase letters
                            <br />* Numbers
                            <br />* One special character: ^$*.[]{}()?-"!@#%&/\,':;|_~`+=
                        </div>,
                        ""
                    ]}
                        autoComplete="current-password"
                    />
                </WithActions>
            </Box>

            {/* Button - Pinned at bottom */}
            {(!disabled) && (
                <Box
                    sx={{
                        flexShrink: 0,
                        pt: 2
                    }}
                >
                    <WithActions forceSpacing={true} isButton={true}>
                        <ButtonPrimary type="submit" disabled={disabled || isLoading}>
                            {isLoading ? 'Signing In...' : 'Sign In'}
                        </ButtonPrimary>
                    </WithActions>
                </Box>
            )}
        </Box>
    );
};

export default LoginForm;
